import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "@fg/icons";
import { connectParityApi, type MyStatus } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { EmojiPickerTrigger } from "../EmojiPicker";

export const STATUS_MESSAGE_MAX = 100;

type ClearAfter = "keep" | "never" | "1h" | "4h" | "today" | "week";

const QUICK_EMOJI = ["📅", "🏠", "🌴", "🤒", "🍽️", "🚗", "✈️", "🎧", "🎯", "💬"];

/** When a "Clear after" choice ends, in the user's own time zone. Null = never. */
function expiryFor(choice: ClearAfter, current: string | null | undefined): string | null {
    const now = new Date();
    switch (choice) {
        case "keep": return current ?? null;
        case "never": return null;
        case "1h": return new Date(now.getTime() + 60 * 60_000).toISOString();
        case "4h": return new Date(now.getTime() + 4 * 60 * 60_000).toISOString();
        case "today": {
            const end = new Date(now);
            end.setHours(23, 59, 59, 999);
            return end.toISOString();
        }
        case "week": {
            // The end of Sunday — "This week" as Teams means it.
            const end = new Date(now);
            end.setDate(now.getDate() + ((7 - now.getDay()) % 7));
            end.setHours(23, 59, 59, 999);
            return end.toISOString();
        }
    }
}

/**
 * Teams' "Set status message": up to 100 characters with an optional emoji,
 * "Clear after", and "Show when people message me". Saved on the server and
 * pushed to everyone in your organisation over the hub.
 */
export function StatusMessageDialog({ current, onClose }: { current: MyStatus | undefined; onClose: () => void }) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const [message, setMessage] = useState(current?.message ?? "");
    const [emoji, setEmoji] = useState(current?.emoji ?? "");
    const [clearAfter, setClearAfter] = useState<ClearAfter>(current?.message && current.expiresAt ? "keep" : "never");
    const [showWhenMessaged, setShowWhenMessaged] = useState(Boolean(current?.showWhenMessaged));
    const [busy, setBusy] = useState<"save" | "clear" | null>(null);
    const [error, setError] = useState<string | null>(null);

    const save = async (clear: boolean) => {
        setBusy(clear ? "clear" : "save");
        setError(null);
        try {
            const text = clear ? "" : message.trim();
            const next = await connectParityApi.setStatusMessage({
                message: text,
                emoji: text && emoji ? emoji : null,
                expiresAt: text ? expiryFor(clearAfter, current?.expiresAt) : null,
                showWhenMessaged: Boolean(text) && showWhenMessaged
            }, token);
            qc.setQueryData(["my-status", token], next);
            onClose();
        } catch {
            setError("Couldn't save your status message. Try again.");
        } finally {
            setBusy(null);
        }
    };

    const keepLabel = current?.expiresAt
        ? `Keep: until ${new Date(current.expiresAt).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}`
        : null;
    const remaining = STATUS_MESSAGE_MAX - message.length;

    return (
        <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
            <DialogContent className="w-[94vw] max-w-md gap-4 p-5">
                <div>
                    <DialogTitle className="text-base">Set status message</DialogTitle>
                    <DialogDescription className="mt-1 text-xs">
                        People in your organisation see it next to your name.
                    </DialogDescription>
                </div>

                <form
                    className="space-y-3"
                    onSubmit={(e) => { e.preventDefault(); if (message.trim()) void save(false); }}
                >
                    <div className="flex items-start gap-2">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border text-lg" aria-label={emoji ? `Emoji ${emoji}` : "No emoji"}>
                            {emoji || <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                        <div className="min-w-0 flex-1">
                            <label htmlFor="status-message" className="sr-only">Status message</label>
                            <textarea
                                id="status-message"
                                value={message}
                                maxLength={STATUS_MESSAGE_MAX}
                                onChange={(e) => setMessage(e.target.value.replace(/\n/g, " "))}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        if (message.trim()) void save(false);
                                    }
                                }}
                                rows={2}
                                autoFocus
                                placeholder="What's happening? e.g. In workshops until 3 PM"
                                aria-describedby="status-message-count"
                                className="w-full resize-none rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            />
                            <div id="status-message-count" className={cn("text-right text-[11px]", remaining < 10 ? "text-amber-600" : "text-muted-foreground")}>
                                {remaining} characters left
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Choose an emoji">
                        {QUICK_EMOJI.map((e) => (
                            <button
                                key={e}
                                type="button"
                                onClick={() => setEmoji((cur) => (cur === e ? "" : e))}
                                aria-pressed={emoji === e}
                                aria-label={`Emoji ${e}`}
                                className={cn(
                                    "flex h-8 w-8 items-center justify-center rounded-md text-base hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                    emoji === e && "bg-primary/15 ring-1 ring-primary"
                                )}
                            >
                                {e}
                            </button>
                        ))}
                        <EmojiPickerTrigger onSelect={(e) => setEmoji(e)} className="h-8 w-8" />
                        {emoji ? (
                            <button
                                type="button"
                                onClick={() => setEmoji("")}
                                aria-label="Remove emoji"
                                title="Remove emoji"
                                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        ) : null}
                    </div>

                    <label className="block text-sm">
                        <span className="mb-1 block text-xs font-medium text-muted-foreground">Clear status message after</span>
                        <select
                            value={clearAfter}
                            onChange={(e) => setClearAfter(e.target.value as ClearAfter)}
                            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                        >
                            {keepLabel ? <option value="keep">{keepLabel}</option> : null}
                            <option value="never">Never</option>
                            <option value="1h">1 hour</option>
                            <option value="4h">4 hours</option>
                            <option value="today">Today</option>
                            <option value="week">This week</option>
                        </select>
                    </label>

                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={showWhenMessaged}
                            onChange={(e) => setShowWhenMessaged(e.target.checked)}
                            className="h-4 w-4 rounded border accent-[hsl(var(--primary))]"
                        />
                        Show when people message me
                    </label>

                    {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}

                    <div className="flex flex-wrap items-center gap-2 pt-1">
                        {current?.message ? (
                            <button
                                type="button"
                                onClick={() => void save(true)}
                                disabled={busy !== null}
                                className="flex h-9 items-center gap-1.5 rounded-md px-3 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50"
                            >
                                {busy === "clear" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                Clear status message
                            </button>
                        ) : null}
                        <div className="ml-auto flex gap-2">
                            <button type="button" onClick={onClose} disabled={busy !== null} className="h-9 rounded-md px-3 text-sm hover:bg-accent disabled:opacity-50">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={busy !== null || !message.trim()}
                                className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                            >
                                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                Done
                            </button>
                        </div>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
